import React, { useState, useEffect, useRef, useCallback } from 'react';
import mascotGif from '../assets/gif/mascota-avatar.gif';

const QUICK_CHIPS = [
  { label: 'Ver contenido', text: '¿Cómo veo el contenido?' },
  { label: 'Subir contenido', text: '¿Cómo subo contenido a la plataforma?' },
  { label: 'Temáticas', text: '¿Qué temáticas hay disponibles?' },
  { label: 'Contacto', text: '¿Cómo contacto al soporte o a un creador?' },
];

// ── Config del botón arrastrable ──
const BTN_SIZE = 72;
const EDGE_MARGIN = 8;
const DRAG_THRESHOLD = 6; // px de movimiento antes de considerarlo "arrastre" y no "click"
const PANEL_W = 320;
const PANEL_MAX_H = 480;
const PANEL_GAP = 16;
const STORAGE_KEY = 'edeli_chat_btn_pos';

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

function getDefaultPos() {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  return {
    x: window.innerWidth - BTN_SIZE - 24,
    y: window.innerHeight - BTN_SIZE - 24,
  };
}

export default function FloatingChat({ username = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `¡Hola${username ? ' ' + username : ''}! ¿En qué puedo ayudarte hoy?`,
      showChips: true,
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const [showLabel, setShowLabel] = useState(false);
  const msgsRef = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef([]);

  // ── Estado de posición / arrastre del botón ──
  const [btnPos, setBtnPos] = useState(getDefaultPos);
  const [isDragging, setIsDragging] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  const posRef = useRef(btnPos);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });

  const clampPos = useCallback((x, y) => ({
    x: clamp(x, EDGE_MARGIN, window.innerWidth - BTN_SIZE - EDGE_MARGIN),
    y: clamp(y, EDGE_MARGIN, window.innerHeight - BTN_SIZE - EDGE_MARGIN),
  }), []);

  // Cargar posición guardada (si existe) al montar
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          setBtnPos(clampPos(parsed.x, parsed.y));
        }
      }
    } catch {
      // localStorage no disponible o dato corrupto: se ignora y se usa la posición default
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { posRef.current = btnPos; }, [btnPos]);

  // Mantener el botón dentro de la pantalla si esta cambia de tamaño (rotar el cel, resize, etc.)
  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      setBtnPos((prev) => clampPos(prev.x, prev.y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampPos]);

  useEffect(() => {
    const timer = setTimeout(() => setShowLabel(true), 2000);
    const timer2 = setTimeout(() => setShowLabel(false), 6000);
    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, []);

  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(prev => !prev);
    setShowBadge(false);
    setShowLabel(false);
  };

  // ── Handlers de arrastre (pointer events: funcionan con mouse, touch y pen) ──
  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: posRef.current.x,
      origY: posRef.current.y,
      moved: false,
    };
  };

  const handlePointerMove = (e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (!dragState.current.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      dragState.current.moved = true;
      setIsDragging(true);
    }
    if (dragState.current.moved) {
      const next = clampPos(dragState.current.origX + dx, dragState.current.origY + dy);
      setBtnPos(next);
    }
  };

  const handlePointerUp = () => {
    if (!dragState.current.dragging) return;
    const wasMoved = dragState.current.moved;
    dragState.current.dragging = false;
    setIsDragging(false);
    if (wasMoved) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current));
      } catch {
        // si falla el guardado, no pasa nada grave: solo no se recuerda la posición
      }
    } else {
      // No hubo arrastre real -> fue un click/tap normal
      toggleChat();
    }
  };

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isLoading) return;
    setInput('');

    const userMsg = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMsg]);
    historyRef.current = [...historyRef.current, { role: 'user', content: trimmed }];
    setIsLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: historyRef.current }),
      });

      const data = await res.json();
      const reply = data?.content?.[0]?.text || 'Lo siento, hubo un error. Intenta de nuevo.';
      historyRef.current = [...historyRef.current, { role: 'assistant', content: reply }];
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Error de conexión. Por favor intenta nuevamente.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Convierte URLs en links clickeables — wa.me muestra botón WhatsApp
  const renderContent = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#f0c060', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {part.includes('wa.me') ? '📲 Contactar por WhatsApp' : part}
        </a>
      ) : part
    );
  };

  const isMobile = viewport.w <= 480;

  // El botón se dibuja con left/top absolutos según btnPos (arrastrable). En vez de
  // bottom/right fijos del CSS, sobreescribimos esas propiedades vía estilo inline.
  const floatBtnStyle = {
    left: btnPos.x,
    top: btnPos.y,
    right: 'auto',
    bottom: 'auto',
    touchAction: 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const introLabelStyle = isMobile
    ? undefined
    : {
        left: 'auto',
        right: clamp(viewport.w - btnPos.x + 12, 12, viewport.w - 40),
        bottom: clamp(viewport.h - (btnPos.y + BTN_SIZE / 2) - 12, 12, viewport.h - 40),
        top: 'auto',
      };

  // El panel se reubica junto al botón: arriba si hay espacio, si no abajo; y se
  // mantiene siempre dentro de la pantalla horizontalmente. En móvil se deja el
  // comportamiento original (fijo abajo, ancho completo) definido en el CSS.
  const panelStyle = (() => {
    if (isMobile) return undefined;
    let right = viewport.w - (btnPos.x + BTN_SIZE);
    right = clamp(right, 12, Math.max(12, viewport.w - PANEL_W - 12));

    const spaceAbove = btnPos.y;
    const spaceBelow = viewport.h - (btnPos.y + BTN_SIZE);
    const openAbove = spaceAbove >= PANEL_MAX_H + PANEL_GAP || spaceAbove >= spaceBelow;

    return openAbove
      ? { right, bottom: viewport.h - btnPos.y + PANEL_GAP, top: 'auto', left: 'auto' }
      : { right, top: btnPos.y + BTN_SIZE + PANEL_GAP, bottom: 'auto', left: 'auto' };
  })();

  return (
    <>
      <style>{`
        .edeli-chat-wrap * { box-sizing: border-box; }
        .edeli-float-btn {
          position: fixed; bottom: 24px; right: 24px;
          width: 72px; height: 72px; border-radius: 50%;
          background: #1a1a2e; border: 2.5px solid #f0c060;
          cursor: grab; display: flex; align-items: center; justify-content: center;
          z-index: 9999; animation: edeli-float 3s ease-in-out infinite;
          transition: transform 0.2s; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          user-select: none; -webkit-user-select: none;
        }
        .edeli-float-btn:hover { transform: scale(1.08); }
        .edeli-float-btn.is-open { animation: none; }
        .edeli-float-btn.is-dragging { animation: none; transition: none; transform: scale(1.06); }
        @keyframes edeli-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        .edeli-pulse {
          position: absolute; border-radius: 50%;
          border: 2px solid #f0c060;
          animation: edeli-pulse-out 2.2s ease-out infinite; pointer-events: none;
        }
        @keyframes edeli-pulse-out {
          0% { width: 72px; height: 72px; opacity: 0.6; top: 0; left: 0; }
          100% { width: 116px; height: 116px; opacity: 0; top: -22px; left: -22px; }
        }
        .edeli-badge {
          position: absolute; top: -4px; right: -4px;
          width: 18px; height: 18px; background: #ef4444; border-radius: 50%;
          color: #fff; font-size: 10px; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          font-family: sans-serif; z-index: 2;
          animation: edeli-badge-pop 0.4s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes edeli-badge-pop { 0% { transform: scale(0); } 100% { transform: scale(1); } }
        .edeli-intro-label {
          position: fixed; bottom: 46px; right: 104px;
          background: #1a1a2e; color: #f0c060; font-size: 12px; font-weight: 700;
          padding: 7px 14px; border-radius: 20px; z-index: 9998;
          border: 1.5px solid #f0c060;
          white-space: nowrap; font-family: sans-serif;
          animation: edeli-label-in 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
          pointer-events: none; box-shadow: 0 2px 14px rgba(240,192,96,0.35);
        }
        @keyframes edeli-label-in {
          0% { opacity: 0; transform: translateX(10px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        /* ── Mascara circular SOLO para el icono (gif/X), asi el badge y el aro pulsante no se cortan ── */
        .edeli-btn-icon-mask {
          position: absolute; inset: 0; border-radius: 50%; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
        }

        /* ── Icono del boton: el GIF del mascota y la X se cruzan con un efecto de "se va" ── */
        .edeli-btn-icon-layer {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.38s cubic-bezier(0.34,1.56,0.64,1), opacity 0.24s ease;
        }
        .edeli-mascot {
          width: 96%; height: 96%; object-fit: contain; pointer-events: none;
        }
        .edeli-btn-icon-layer.mascot-layer {
          opacity: 1; transform: scale(1) rotate(0deg);
        }
        .edeli-float-btn.is-open .edeli-btn-icon-layer.mascot-layer {
          opacity: 0; transform: scale(0.15) rotate(35deg) translateY(6px);
        }
        .edeli-btn-icon-layer.close-layer {
          opacity: 0; transform: scale(0.3) rotate(-35deg);
        }
        .edeli-float-btn.is-open .edeli-btn-icon-layer.close-layer {
          opacity: 1; transform: scale(1) rotate(0deg);
        }

        .edeli-panel {
          position: fixed; bottom: 104px; right: 24px;
          width: 320px; max-height: 480px; background: #1a1a2e;
          border-radius: 18px; border: 1px solid rgba(240,192,96,0.25);
          display: flex; flex-direction: column; z-index: 9998; overflow: hidden;
          transform-origin: bottom right;
          transform: scale(0.85) translateY(16px); opacity: 0; pointer-events: none;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.22s ease;
          box-shadow: 0 8px 40px rgba(0,0,0,0.35);
        }
        .edeli-panel.open { transform: scale(1) translateY(0); opacity: 1; pointer-events: all; }
        @media (max-width: 480px) {
          .edeli-panel { right: 12px; left: 12px; width: auto; bottom: 96px; max-height: 65vh; }
          .edeli-float-btn { bottom: 18px; right: 18px; }
          .edeli-intro-label { bottom: 40px; right: 96px; }
        }
        .edeli-panel-head {
          background: #0f0f1e; padding: 12px 14px;
          display: flex; align-items: center; gap: 9px;
          border-bottom: 1px solid rgba(240,192,96,0.12); flex-shrink: 0;
        }
        .edeli-ava {
          width: 30px; height: 30px; border-radius: 50%; background: #f0c060;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 600; color: #1a1a2e; flex-shrink: 0; font-family: sans-serif;
          overflow: hidden;
        }
        .edeli-ava img { width: 100%; height: 100%; object-fit: contain; }
        .edeli-head-name { color: #f0c060; font-size: 13px; font-weight: 600; font-family: sans-serif; margin: 0; }
        .edeli-head-status {
          color: #4ade80; font-size: 10px; font-family: sans-serif;
          display: flex; align-items: center; gap: 4px; margin: 2px 0 0;
        }
        .edeli-head-status::before {
          content: ''; width: 5px; height: 5px; border-radius: 50%;
          background: #4ade80; display: inline-block;
        }
        .edeli-close {
          margin-left: auto; background: none; border: none; color: #9ca3af;
          cursor: pointer; font-size: 18px; line-height: 1; padding: 2px 5px; border-radius: 4px;
        }
        .edeli-close:hover { color: #fff; background: rgba(255,255,255,0.08); }
        .edeli-msgs {
          flex: 1; overflow-y: auto; padding: 14px 12px;
          display: flex; flex-direction: column; gap: 12px;
          scrollbar-width: thin; scrollbar-color: #2d2d50 transparent;
        }
        .edeli-msgs::-webkit-scrollbar { width: 3px; }
        .edeli-msgs::-webkit-scrollbar-thumb { background: #2d2d50; border-radius: 2px; }
        .edeli-msg-row { display: flex; align-items: flex-end; gap: 7px; }
        .edeli-msg-row.user { flex-direction: row-reverse; }
        .edeli-msg-ava {
          width: 24px; height: 24px; border-radius: 50%; background: #f0c060;
          color: #1a1a2e; font-size: 10px; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; font-family: sans-serif; overflow: hidden;
        }
        .edeli-msg-ava img { width: 100%; height: 100%; object-fit: contain; }
        .edeli-msg-ava.user { background: #374151; color: #e5e7eb; }
        .edeli-bubble { max-width: 82%; padding: 9px 12px; font-size: 13px; line-height: 1.55; font-family: sans-serif; }
        .edeli-bubble.bot { background: #252545; color: #e5e7eb; border-radius: 4px 12px 12px 12px; }
        .edeli-bubble.user { background: #f0c060; color: #1a1a2e; font-weight: 500; border-radius: 12px 12px 4px 12px; }
        .edeli-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
        .edeli-chip {
          background: transparent; border: 1px solid rgba(240,192,96,0.4);
          border-radius: 20px; padding: 4px 10px; font-size: 11px; color: #f0c060;
          cursor: pointer; font-family: sans-serif; transition: all 0.15s;
        }
        .edeli-chip:hover { background: rgba(240,192,96,0.1); border-color: #f0c060; }
        .edeli-typing { display: flex; align-items: center; gap: 4px; padding: 4px 0; }
        .edeli-typing span {
          width: 5px; height: 5px; border-radius: 50%; background: #9ca3af;
          animation: edeli-dot 1.2s infinite;
        }
        .edeli-typing span:nth-child(2) { animation-delay: 0.2s; }
        .edeli-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes edeli-dot {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-5px); }
        }
        .edeli-footer {
          padding: 10px 12px; border-top: 1px solid rgba(240,192,96,0.1);
          display: flex; gap: 7px; align-items: center; flex-shrink: 0; background: #0f0f1e;
        }
        .edeli-input {
          flex: 1; background: #252545; border: 1px solid rgba(240,192,96,0.2);
          border-radius: 20px; padding: 8px 14px; font-size: 13px; color: #e5e7eb;
          font-family: sans-serif; outline: none; transition: border-color 0.2s;
        }
        .edeli-input:focus { border-color: rgba(240,192,96,0.6); }
        .edeli-input::placeholder { color: #6b7280; }
        .edeli-send {
          width: 34px; height: 34px; border-radius: 50%; background: #f0c060;
          border: none; cursor: pointer; display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: opacity 0.2s, transform 0.15s;
        }
        .edeli-send:hover { opacity: 0.85; }
        .edeli-send:active { transform: scale(0.93); }
        .edeli-send:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

      <div className="edeli-chat-wrap">
        {showLabel && !isDragging && (
          <div className="edeli-intro-label" style={introLabelStyle}>¡Click aquí! 👋 ¿En qué te ayudo?</div>
        )}

        <div
          className={`edeli-float-btn${isOpen ? ' is-open' : ''}${isDragging ? ' is-dragging' : ''}`}
          style={floatBtnStyle}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          role="button"
          aria-label={isOpen ? 'Cerrar asistente virtual' : 'Abrir asistente virtual (mantén presionado para moverlo)'}
        >
          {!isOpen && <div className="edeli-pulse" />}
          {showBadge && !isOpen && <div className="edeli-badge">1</div>}

          {/* Mascara circular: dentro va la mascota y la X, aqui SI se recorta en circulo */}
          <div className="edeli-btn-icon-mask">
            {/* Mascota animada: visible cuando el chat esta cerrado, se encoge y gira al abrir */}
            <div className="edeli-btn-icon-layer mascot-layer">
              <img src={mascotGif} alt="Asistente IA" className="edeli-mascot" draggable={false} />
            </div>

            {/* Icono X: aparece cuando el chat esta abierto */}
            <div className="edeli-btn-icon-layer close-layer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#f0c060">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </div>
          </div>
        </div>

        <div className={`edeli-panel${isOpen ? ' open' : ''}`} style={panelStyle}>
          <div className="edeli-panel-head">
            <div className="edeli-ava"><img src={mascotGif} alt="" /></div>
            <div>
              <p className="edeli-head-name">Asistente virtual</p>
              <p className="edeli-head-status">En línea ahora</p>
            </div>
            <button className="edeli-close" onClick={toggleChat} aria-label="Cerrar">✕</button>
          </div>

          <div className="edeli-msgs" ref={msgsRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`edeli-msg-row${msg.role === 'user' ? ' user' : ''}`}>
                <div className={`edeli-msg-ava${msg.role === 'user' ? ' user' : ''}`}>
                  {msg.role === 'user' ? (username ? username[0].toUpperCase() : 'U') : <img src={mascotGif} alt="" />}
                </div>
                <div className={`edeli-bubble${msg.role === 'user' ? ' user' : ' bot'}`}>
                  {renderContent(msg.content)}
                  {msg.showChips && (
                    <div className="edeli-chips">
                      {QUICK_CHIPS.map((chip) => (
                        <button key={chip.label} className="edeli-chip" onClick={() => sendMessage(chip.text)}>
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="edeli-msg-row">
                <div className="edeli-msg-ava"><img src={mascotGif} alt="" /></div>
                <div className="edeli-bubble bot">
                  <div className="edeli-typing"><span /><span /><span /></div>
                </div>
              </div>
            )}
          </div>

          <div className="edeli-footer">
            <input
              ref={inputRef}
              className="edeli-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Escribe tu consulta..."
              maxLength={400}
              disabled={isLoading}
            />
            <button
              className="edeli-send"
              onClick={() => sendMessage()}
              disabled={isLoading || !input.trim()}
              aria-label="Enviar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#1a1a2e">
                <path d="M2 21L23 12 2 3v7l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}